import { oktaLogin, oktaLogout, oktaRenew } from '@/services/auth/auth.utils.js';
import regex from '@/consts/regex.const.js';
import jllOktaAuthManager from 'jll-okta-auth-manager';
import Console from '@/shared/console';
import pixAxios from '@/axios/pixAxios';

const redirectStorageKey = 'PIX-redirectURL';
const pixAuthTokenKey = 'pix-okta-auth-token';
const oktaAuthTokenKey = 'okta-token-storage';

const authModule = {
  namespaced: true,

  state: {
    auth: {
      jwtToken: null, // Authorization jwt token.
    },
    isRenewingToken: false,
    refreshingTokenPromise: null,
  },

  mutations: {
    setToken(state, jwt) {
      state.auth.jwtToken = jwt;
    },
    setIsRenewingToken(state, newVal) {
      state.isRenewingToken = newVal;
    },
    setRefreshingTokenPromise(state, newVal) {
      state.refreshingTokenPromise = newVal;
    },
  },

  actions: {
    async login({ commit, state, dispatch }) {
      await jllOktaAuthManager.initOktaAuthManager({
        tenantId: GlobalPixInitProps.oktaTenantId,
        clientId: GlobalPixInitProps.oktaClientId,
        redirectUri: GlobalPixInitProps.oktaRedirectUri,
        logoutUri: GlobalPixInitProps.oktaLogoutURL,
        isDebug: true,
        redirectStorageKey,
      });

      await oktaLogin({ state, commit, dispatch, redirectStorageKey, pixAuthTokenKey });
    },

    logout({ commit }) {
      oktaLogout({ commit, pixAuthTokenKey });
    },

    renew({ state, commit }) {
      return oktaRenew({ state, commit, pixAuthTokenKey });
    },

    unloadPixAuthToken() {
      localStorage.removeItem(pixAuthTokenKey); // remove our token from localstorage
    },

    unloadOktaAuthToken() {
      localStorage.removeItem(oktaAuthTokenKey); // remove okta tokenManager from localstorage
    },

    /**
     * Hace la llamada para traer la configuracion del usuario
     * configuracion como formato de fecha, hora, unidad de medida.
     * @param {string} instance instancia del usuario.
     */
    async getUserSettings({ commit, getters, rootGetters }) {
      try {
        const { data } = await pixAxios.get(`api/MongoUserSettings/GetMySettings?instance=${getters.fullInstance}`);

        /* Se lanza para la gestión en el vuex */
        commit('userSettings', data, { root: true });
      } catch (error) {
        Console.log('Get config user error: ', error);
      }
    },
  },

  getters: {
    isAuthenticated(state) {
      return state.auth.jwtToken !== null && state.auth.jwtToken.access_token !== null;
    },

    jwtToken(state) {
      return state.auth.jwtToken;
    },

    /**
     * Devuelve el email del usuario.
     */
    UserName(state) {
      return state.auth.jwtToken.user_name;
    },

    /**
     * Devuelve el email del usuario, en minúsculas.
     */
    lowerCasedUserName(state) {
      return state?.auth?.jwtToken?.user_name?.toLowerCase();
    },

    /**
     * Devuelve el nombre del usuario.
     */
    UserFullName(state) {
      return state.auth.jwtToken.user_full_name;
    },

    /**
     * Devuelve si el usuario tiene un rol dado.
     */
    isInRole: (state, getters) => (role) => {
      if (getters.isAuthenticated) {
        const roles = state.auth.jwtToken.roles;
        if (Array.isArray(roles) && roles.length > 0) {
          const idx = roles.indexOf(role);
          return idx >= 0;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },

    /* Devuelve si el usuario tiene alguno de los roles dados. */
    isInRoleArr: (state, getters) => (rolesArr) => {
      let bFound = false;
      /* Creamos un listado con todos los roles */
      const rolesList = rolesArr.split(',');
      /* Recorremos los roles para ver si alguno coincide con los asignados al usuario */
      for (let lCnt = 0; lCnt < rolesList.length && !bFound; lCnt += 1) {
        if (getters.isInRole(rolesList[lCnt].trim())) {
          bFound = true;
          break;
        }
      }
      return bFound;
    },

    /**
     * Getter para obtener la instancia del state.
     */
    instance(state) {
      return state.auth.jwtToken.instance;
    },

    /**
     * Getter para obtener el valor de la instancia
     * preparado para las llamadas a los servicios de PIX.
     */
    fullInstance(state) {
      const instanceShortString = state.auth.jwtToken?.instance?.toUpperCase();
      if (instanceShortString && regex.INSTANCE.test(instanceShortString)) {
        return instanceShortString;
      } else if (instanceShortString) {
        return `INSTANCE_${instanceShortString}`;
      } else {
        return null;
      }
    },
  },
};

export default authModule;
